var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import without from 'lodash/without';
import { DEFAULT_SOURCE_REDUCER_STATE } from './constants';
import { ensureSafeChaining, getObjNthItem } from './helpers';
var fetchSuccess = function (state, payload, options) {
    var source = payload.source, normalized = payload.normalized, paginator = payload.paginator, isLoadMore = payload.isLoadMore, rest = __rest(payload, ["source", "normalized", "paginator", "isLoadMore"]);
    if (!source) {
        return;
    }
    var allIds = normalized.result;
    if ((options === null || options === void 0 ? void 0 : options.sortComparer) && normalized.entities[options.entityName]) {
        allIds = Object.values(normalized.entities[options.entityName])
            .sort(options.sortComparer)
            .map(function (item) { return item.id; });
    }
    ensureSafeChaining(state, ['sources', source], DEFAULT_SOURCE_REDUCER_STATE);
    state.sources[source] = __assign(__assign(__assign({}, state.sources[source]), { paginator: paginator, allIds: isLoadMore ? __spreadArray([], __read(new Set(__spreadArray(__spreadArray([], __read((state.sources[source].allIds || [])), false), __read(allIds), false))), false) : allIds, lastRequestAt: Date.now() / 1000 }), rest);
};
var deleteSuccess = function (state, id) {
    Object.keys(state.sources).forEach(function (stateKey) {
        ensureSafeChaining(state, ['sources', stateKey, 'allIds'], []);
        state.sources[stateKey].allIds = without(state.sources[stateKey].allIds, id);
    });
};
var addIdsToSources = function (state, ids, sources, append) {
    if (append === void 0) { append = false; }
    var add = function (source, id) {
        var _a;
        if (((_a = state.sources[source].allIds) === null || _a === void 0 ? void 0 : _a.indexOf(id)) === -1) {
            if (append) {
                state.sources[source].allIds.push(id);
            }
            else {
                state.sources[source].allIds.unshift(id);
            }
        }
    };
    sources.forEach(function (source) {
        if (ids) {
            ensureSafeChaining(state, ['sources', source, 'allIds'], []);
            if (isArray(ids)) {
                ids.forEach(function (id) { return add(source, id); });
            }
            else {
                add(source, ids);
            }
        }
    });
};
var removeIdsFromSources = function (state, ids, sources) {
    sources.forEach(function (source) {
        var _a, _b;
        if (ids) {
            ensureSafeChaining(state, ['sources', source, 'allIds'], []);
            if (isArray(ids)) {
                state.sources[source].allIds = (_a = state.sources[source].allIds) === null || _a === void 0 ? void 0 : _a.filter(function (i) { return ids.indexOf(i) === -1; });
            }
            else {
                state.sources[source].allIds = (_b = state.sources[source].allIds) === null || _b === void 0 ? void 0 : _b.filter(function (i) { return i !== ids; });
            }
        }
    });
};
var requestOK = function (result, action) { return action.fulfilled.match(result); };
var allRequestsOK = function () {
    var resultActionPair = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        resultActionPair[_i] = arguments[_i];
    }
    return resultActionPair.every(function (_a) {
        var _b = __read(_a, 2), result = _b[0], action = _b[1];
        var isOk = requestOK(result, action);
        if (!isOk) {
            // eslint-disable-next-line no-console
            console.error('Check carefully your result and action pair!!');
        }
        return isOk;
    });
};
var requestFailed = function (result, action) { return action.rejected.match(result); };
var createSelectAllBySource = function (subStateName, selectEntities, sources) {
    return sources.map(function (source) {
        return createSelector([selectEntities, function (state) { var _a; return ((_a = state[subStateName]) === null || _a === void 0 ? void 0 : _a.sources[source]) || DEFAULT_SOURCE_REDUCER_STATE; }], function (entities, _a) {
            var _b = _a.allIds, allIds = _b === void 0 ? [] : _b, rest = __rest(_a, ["allIds"]);
            return (__assign({ data: allIds.map(function (id) { return entities[id]; }).filter(function (i) { return i !== undefined; }) }, rest));
        });
    });
};
// Upsert ignoring nested object!
var upsertMany = function (adapter, state, entities) {
    if (entities === void 0) { entities = {}; }
    var addedEntities = {};
    Object.keys(entities).forEach(function (entityId) {
        var entity = entities[entityId];
        if (!state.entities[entityId]) {
            addedEntities[entityId] = entity;
        }
        else {
            var existedEntity_1 = state.entities[entityId];
            var shouldUpdate_1 = true;
            if (isPlainObject(entity)) {
                shouldUpdate_1 = false;
                Object.keys(entity).forEach(function (prop) {
                    var value = entity[prop];
                    if (!isPlainObject(value) && value !== existedEntity_1[prop]) {
                        shouldUpdate_1 = true;
                    }
                });
            }
            if (shouldUpdate_1) {
                adapter.updateOne(state, entity);
            }
        }
    });
    adapter.addMany(state, addedEntities);
};
// Upsert mutate means always replace existing object! Always trigger re-render
// Try to avoid using it
var upsertManyMutably = function (adapter, state, entities) {
    if (entities === void 0) { entities = {}; }
    adapter.upsertMany(state, entities);
};
var upsertOneMutably = function (adapter, state, entities) {
    if (entities === void 0) { entities = {}; }
    var entity = getObjNthItem(entities, 1);
    if (entity) {
        var existedEntity = state.entities[entity.id];
        if (!isEqual(entity, existedEntity)) {
            adapter.upsertOne(state, entity);
        }
    }
};
export { fetchSuccess, deleteSuccess, addIdsToSources, removeIdsFromSources, requestOK, requestFailed, createSelectAllBySource, upsertMany, upsertOneMutably, upsertManyMutably, allRequestsOK, };
